import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Filter, Calendar, CreditCard, User, Package, Users, Clock,
  ChevronDown, ChevronUp, RefreshCw, X, Search
} from 'lucide-react';

const datePresets = [
  { label: 'Today', days: 0 },
  { label: 'Yesterday', days: -1 },
  { label: 'Last 7 Days', days: -7 },
  { label: 'Last 30 Days', days: -30 },
  { label: 'This Month', days: 'month' },
  { label: 'Last Month', days: 'lastMonth' },
  { label: 'This Year', days: 'year' },
];

const paymentMethods = ['All', 'Cash', 'UPI', 'Card', 'Wallet', 'Credit'];
const statuses = ['All', 'Completed', 'Pending', 'Cancelled', 'Refunded'];

export default function ReportFilters({
  onApply,
  onReset,
  customers = [],
  categories = [],
  staff = [],
  loading = false,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: 'last-7',
    startDate: '',
    endDate: '',
    paymentMethod: 'All',
    customer: '',
    category: '',
    staff: '',
    status: 'All',
  });

  const handlePreset = (preset) => {
    const now = new Date();
    let start, end;

    if (preset.days === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = now;
    } else if (preset.days === 'lastMonth') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (preset.days === 'year') {
      start = new Date(now.getFullYear(), 0, 1);
      end = now;
    } else {
      end = now;
      start = new Date(now);
      start.setDate(start.getDate() + preset.days);
    }

    setFilters((prev) => ({
      ...prev,
      dateRange: preset.label,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    }));
  };

  const handleApply = () => {
    onApply?.(filters);
  };

  const handleReset = () => {
    setFilters({
      dateRange: 'last-7',
      startDate: '',
      endDate: '',
      paymentMethod: 'All',
      customer: '',
      category: '',
      staff: '',
      status: 'All',
    });
    onReset?.();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-primary-600 dark:text-primary-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filters</h3>
        </div>
        {collapsed ? (
          <ChevronDown size={16} className="text-gray-400" />
        ) : (
          <ChevronUp size={16} className="text-gray-400" />
        )}
      </button>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                  <Calendar size={12} /> Date Range
                </label>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {datePresets.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => handlePreset(preset)}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-colors ${
                        filters.dateRange === preset.label
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value, dateRange: '' }))}
                    className="flex-1 px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:border-primary-500"
                  />
                  <span className="text-xs text-gray-400 self-center">to</span>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value, dateRange: '' }))}
                    className="flex-1 px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                    <CreditCard size={11} /> Payment Method
                  </label>
                  <select
                    value={filters.paymentMethod}
                    onChange={(e) => setFilters((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                    className="w-full px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none"
                  >
                    {paymentMethods.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                    <User size={11} /> Customer
                  </label>
                  <select
                    value={filters.customer}
                    onChange={(e) => setFilters((prev) => ({ ...prev, customer: e.target.value }))}
                    className="w-full px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none"
                  >
                    <option value="">All Customers</option>
                    {customers.map((c) => (
                      <option key={c.customerId || c._id} value={c.customerId || c._id}>{c.fullName || c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                    <Package size={11} /> Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none"
                  >
                    <option value="">All Categories</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                    <Users size={11} /> Staff
                  </label>
                  <select
                    value={filters.staff}
                    onChange={(e) => setFilters((prev) => ({ ...prev, staff: e.target.value }))}
                    className="w-full px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none"
                  >
                    <option value="">All Staff</option>
                    {staff.map((s) => (
                      <option key={s.userId || s._id} value={s.userId || s._id}>{s.name || s.fullName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                  <Clock size={11} /> Status
                </label>
                <div className="flex gap-1.5 flex-wrap">
                  {statuses.map((s) => (
                    <button
                      key={s}
                      onClick={() => setFilters((prev) => ({ ...prev, status: s }))}
                      className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                        filters.status === s
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleReset}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} />
                  Reset
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleApply}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Search size={12} />
                  )}
                  Apply Filters
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
